import { bankQuestionService, BankQuestion, BankQuizData } from '../bankQuestionService_moi'

type ExamType = 'GKI' | 'GKII' | 'CKI' | 'CKII'
type QuestionGroup = 'TN' | 'DS' | 'TL' | 'TLU'
type Difficulty = 'M1' | 'M2' | 'M3'

interface GenerateExamOptions {
  examType: ExamType
  lop?: number
  title?: string
  durationMinutes?: number
  passingScore?: number
  planOverrides?: Partial<Record<QuestionGroup, GroupPlan>>
}

interface GroupPlan {
  total: number
  distribution: Record<Difficulty, number>
}

const QUESTION_PLANS: Record<QuestionGroup, GroupPlan> = {
  TN: {
    total: 12,
    distribution: {
      M1: 8,
      M2: 3,
      M3: 1
    }
  },
  DS: {
    total: 4,
    distribution: {
      M1: 2,
      M2: 1,
      M3: 1
    }
  },
  TL: {
    total: 4,
    distribution: {
      M1: 2,
      M2: 2,
      M3: 0
    }
  },
  TLU: {
    total: 3,
    distribution: {
      M1: 1,
      M2: 1,
      M3: 1
    }
  }
}

const GROUP_SCORE: Record<QuestionGroup, number> = {
  TN: 0.25,
  DS: 1,
  TL: 0.5,
  TLU: 1.0
}

const GROUP_TO_TYPE: Record<QuestionGroup, BankQuestion['type']> = {
  TN: 'trac_nghiem_nhieu_lua_chon',
  DS: 'dung_sai',
  TL: 'tinh_toan',
  TLU: 'tinh_toan'
}

const EXAM_CONFIGS: Record<ExamType, {
  label: string
  chapters: number[]
  defaultDuration: number // minutes
  passingScore: number
}> = {
  GKI: {
    label: 'Đề Giữa Kỳ I',
    chapters: [1],
    defaultDuration: 45,
    passingScore: 5
  },
  CKI: {
    label: 'Đề Cuối Kỳ I',
    chapters: [1, 2],
    defaultDuration: 60,
    passingScore: 5
  },
  GKII: {
    label: 'Đề Giữa Kỳ II',
    chapters: [3],
    defaultDuration: 45,
    passingScore: 5
  },
  CKII: {
    label: 'Đề Cuối Kỳ II',
    chapters: [3, 4],
    defaultDuration: 60,
    passingScore: 5
  }
}

const DIFFICULTY_ORDER: Difficulty[] = ['M1', 'M2', 'M3']

interface ChapterShare {
  chapter: number
  count: number
}

interface MissingQuota {
  group: QuestionGroup
  difficulty: Difficulty
  chapter: number
  missing: number
}

class ExamGeneratorService {
  private poolCache = new Map<string, BankQuestion[]>()

  async generateStandardExam(options: GenerateExamOptions): Promise<BankQuizData> {
    const { examType, lop = 11 } = options
    const config = EXAM_CONFIGS[examType]

    if (!config) {
      throw new Error(`Không hỗ trợ dạng đề ${examType}`)
    }

    this.poolCache.clear()

    const title = options.title || `${config.label} - Lớp ${lop}`
    const durationMinutes = options.durationMinutes ?? config.defaultDuration
    const passingScore = options.passingScore ?? config.passingScore

    const usedIds = new Set<string>()
    const questionsByGroup: BankQuestion[] = []
    const missing: MissingQuota[] = []

    for (const group of Object.keys(QUESTION_PLANS) as QuestionGroup[]) {
      const plan = options.planOverrides?.[group] ?? QUESTION_PLANS[group]
      const groupQuestions = await this.selectQuestionsForGroup({
        group,
        examType,
        lop,
        chapters: config.chapters,
        usedIds,
        missing,
        plan
      })

      if (groupQuestions.length < plan.total) {
        throw new Error(
          `Không đủ câu hỏi ${group} cho đề ${examType}. Cần ${plan.total}, hiện có ${groupQuestions.length}.`
        )
      }

      questionsByGroup.push(...groupQuestions)
    }

    if (missing.length) {
      console.warn('⚠️ Thiếu câu hỏi theo phân bổ yêu cầu:', missing)
    }

    const shuffled = this.shuffleArray(questionsByGroup)
    const totalScore = shuffled.reduce((sum, q) => sum + (q.diem || 0), 0)

    return {
      id: `exam_${examType}_${lop}_${Date.now()}`,
      tieuDe: title,
      moTa: `${config.label} | ${shuffled.length} câu | ${totalScore} điểm tối đa`,
      gioi_han_thoi_gian: durationMinutes * 60,
      diem_dat: passingScore,
      cauHoi: shuffled
    }
  }

  private async selectQuestionsForGroup(params: {
    group: QuestionGroup
    examType: ExamType
    lop: number
    chapters: number[]
    usedIds: Set<string>
    missing: MissingQuota[]
    plan: GroupPlan
  }): Promise<BankQuestion[]> {
    const { group, examType, lop, chapters, usedIds, missing, plan } = params
    const selected: BankQuestion[] = []

    for (const difficulty of DIFFICULTY_ORDER) {
      const required = plan.distribution[difficulty]
      if (!required) continue

      const chapterShares = this.splitAcrossChapters(required, chapters)

      for (const share of chapterShares) {
        if (share.count === 0) continue

        const pool = await this.getQuestionPool(lop, share.chapter, group, difficulty)
        const available = pool.filter(question => !usedIds.has(question.id))

        if (!available.length) {
          missing.push({ group, difficulty, chapter: share.chapter, missing: share.count })
          continue
        }

        const picked = this.takeRandomQuestions(available, Math.min(share.count, available.length))
        picked.forEach(question => usedIds.add(question.id))

        const normalized = picked.map(question =>
          this.prepareQuestion(question, {
            examType,
            group,
            difficulty,
            lop,
            chapter: share.chapter
          })
        )

        selected.push(...normalized)

        if (picked.length < share.count) {
          missing.push({
            group,
            difficulty,
            chapter: share.chapter,
            missing: share.count - picked.length
          })
        }
      }
    }

    return selected
  }

  private async getQuestionPool(
    lop: number,
    chapter: number,
    group: QuestionGroup,
    difficulty: Difficulty
  ): Promise<BankQuestion[]> {
    const cacheKey = `L${lop}_C${chapter}_${group}_${difficulty}`
    if (this.poolCache.has(cacheKey)) {
      return this.poolCache.get(cacheKey) || []
    }

    const pool = await bankQuestionService.getQuestionsByChapterAndDifficulty({
      lop,
      chuong: chapter,
      loai: group,
      mucDo: difficulty
    })

    this.poolCache.set(cacheKey, pool)
    return pool
  }

  private splitAcrossChapters(total: number, chapters: number[]): ChapterShare[] {
    if (!chapters.length) return []
    const base = Math.floor(total / chapters.length)
    let remainder = total % chapters.length

    return chapters.map(chapter => {
      const extra = remainder > 0 ? 1 : 0
      if (remainder > 0) remainder--
      return {
        chapter,
        count: base + extra
      }
    })
  }

  private takeRandomQuestions(pool: BankQuestion[], count: number): BankQuestion[] {
    if (pool.length <= count) {
      return [...pool]
    }
    const shuffled = this.shuffleArray([...pool])
    return shuffled.slice(0, count)
  }

  private prepareQuestion(
    question: BankQuestion,
    context: {
      examType: ExamType
      group: QuestionGroup
      difficulty: Difficulty
      lop: number
      chapter: number
    }
  ): BankQuestion {
    const cloned = this.cloneQuestion(question)
    cloned.type = GROUP_TO_TYPE[context.group]
    cloned.diem = GROUP_SCORE[context.group]
    cloned.examMetadata = {
      examType: context.examType,
      lop: context.lop,
      chuong: context.chapter,
      bai: (question as any).bai || 0,
      group: context.group,
      difficulty: context.difficulty
    }
    return cloned
  }

  private cloneQuestion(question: BankQuestion): BankQuestion {
    return {
      ...question,
      lua_chon: question.lua_chon ? [...question.lua_chon] : undefined,
      goi_y: question.goi_y ? [...question.goi_y] : undefined
    }
  }

  private shuffleArray<T>(items: T[]): T[] {
    const array = [...items]
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
        ;[array[i], array[j]] = [array[j], array[i]]
    }
    return array
  }
}

export const examGeneratorService = new ExamGeneratorService()
export default ExamGeneratorService

